/**
 * A location's settings sections, in display order: the account's groups and
 * section names (accountSections.ts), only the ones a location has, plus its
 * own Services & tags. Financials splits into
 * Pricing and Accounting, matching the account. Gates are locationTabs' rules
 * (see access.ts).
 */
import { canOpenStoreInvoicing } from '../../modals/invoicingSections';
import { canOpenLocationFeeds, canOpenLocationMoney, canOpenPartners, canSeeLocationPolicySurfaces } from './access';
import type { SettingsAccess, SettingsSectionDef } from './types';

const edit = (a: SettingsAccess) => a.isEditMode;
const editAnd = (rule: (a: SettingsAccess) => boolean) => (a: SettingsAccess) => a.isEditMode && rule(a);

export const LOCATION_SECTIONS: readonly SettingsSectionDef[] = [
  { id: 'overview', label: 'Overview', group: '', scope: 'location', save: 'none', visible: edit, keywords: ['setup', 'to-do', 'summary'] },
  {
    id: 'details',
    label: 'Profile',
    group: 'Business',
    scope: 'location',
    save: 'explicit',
    keywords: ['name', 'code', 'address', 'timezone', 'logo', 'production site', 'group'],
  },
  { id: 'people', label: 'People', group: 'Business', scope: 'location', save: 'instant', visible: edit, keywords: ['users', 'staff'] },
  {
    id: 'services',
    label: 'Services & tags',
    group: 'Operations',
    scope: 'location',
    save: 'instant',
    visible: edit,
    tabs: [
      { id: 'departments', label: 'Departments', keywords: ['department order', 'workflow', 'sections', 'service splits', 'split'] },
      { id: 'services', label: 'Services', keywords: ['service catalog', 'price'] },
      { id: 'tags', label: 'Tags' },
    ],
  },
  {
    id: 'work-orders',
    label: 'Work orders',
    group: 'Operations',
    scope: 'location',
    save: 'mixed',
    visible: editAnd(canSeeLocationPolicySurfaces),
    keywords: ['required fields', 'restrictions', 'pin', 'lock after sync'],
  },
  {
    id: 'time-attendance',
    label: 'Time & attendance',
    group: 'Operations',
    scope: 'location',
    save: 'explicit',
    visible: editAnd(canSeeLocationPolicySurfaces),
    tabs: [
      { id: 'geofence', label: 'Clock-in area', keywords: ['geofence', 'radius'] },
      { id: 'safeguards', label: 'Timesheet safeguards', keywords: ['fraud', 'auto logout'] },
    ],
  },
  {
    id: 'pricing',
    label: 'Pricing',
    group: 'Money',
    scope: 'location',
    save: 'mixed',
    visible: editAnd((a) => canOpenLocationMoney(a) && a.hasAccount !== false),
    tabs: [
      { id: 'defaults', label: 'Labor rate & auto-approval', keywords: ['labor rate', 'auto-approval'] },
      { id: 'pricing', label: 'Discounts & fees', keywords: ['discount', 'fee'] },
    ],
  },
  {
    id: 'accounting',
    label: 'Accounting',
    group: 'Money',
    scope: 'location',
    save: 'explicit',
    visible: editAnd(canOpenLocationMoney),
    keywords: ['class', 'quickbooks', 'books'],
  },
  {
    // The account's Invoicing narrowed to this store: Setup (its Who gets
    // what: how its invoices go out, what it does differently, who gets them;
    // saves after Check what changes) for rule editors, and this store's
    // Invoices, Emails and Accounting for whoever may see each.
    id: 'invoicing',
    label: 'Invoicing',
    group: 'Money',
    scope: 'location',
    save: 'checked',
    visible: editAnd((a) => canOpenStoreInvoicing([...a.permissions], a.isSystemAdmin) && a.hasAccount !== false),
    keywords: [
      'invoices',
      'who gets what',
      'contacts',
      'billing',
      'emailed',
      'store different',
      'emails',
      'bounced',
      'accounting',
      'synced',
    ],
  },
  {
    id: 'feeds',
    label: 'Feeds',
    group: 'Connections',
    scope: 'location',
    save: 'instant',
    visible: editAnd(canOpenLocationFeeds),
    keywords: ['inventory', 'vehicle feed'],
  },
  { id: 'partners', label: 'Partners', group: 'Connections', scope: 'location', save: 'instant', visible: editAnd(canOpenPartners) },
];
